import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowUpDown, Eye, GripVertical, Image as ImageIcon, Layers, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fieldTypeOptions } from '../utils/fieldTypes';
import { compressImage } from '../utils/imageCompress';
import { apiRequest } from '../services/api';
import DynamicFormRenderer from './DynamicFormRenderer';
import Modal from './Modal';

// Yerel benzersiz kimlik üretici (dnd-kit eşleştirmesi için)
function createLocalId() {
  return window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

const emptyField = {
  id: null,
  uid: '',
  label: '',
  fieldType: 'text',
  isRequired: false,
  sortOrder: 1,
  placeholder: '',
  options: [],
  pendingOption: '',
  maxFileSizeMb: '',
  width: 'full',
  column: 'left',
};

function createEmptyField(sortOrder = 1) {
  return { ...emptyField, uid: createLocalId(), sortOrder };
}

function createEmptySection(sortOrder = 1) {
  return {
    id: null,
    uid: createLocalId(),
    title: sortOrder === 1 ? 'Genel Bilgiler' : '',
    description: '',
    sortOrder,
    fields: [createEmptyField(1)],
  };
}

// Görsel seçim alanı için string/obje seçenekleri normalize eder
function normalizeImageOption(option) {
  if (option && typeof option === 'object') {
    return { label: option.label || '', image: option.image || '' };
  }
  if (typeof option === 'string') {
    try {
      const parsed = JSON.parse(option);
      if (parsed && typeof parsed === 'object') {
        return { label: parsed.label || '', image: parsed.image || '' };
      }
    } catch (error) {
      // JSON değilse düz metin etiketi olarak kabul ediyoruz
    }
    return { label: option, image: '' };
  }
  return { label: '', image: '' };
}

// Backend `List<string>` beklediği için görsel seçeneklerini JSON string'e çeviriyoruz
function buildOptionsForSubmit(field) {
  if (field.fieldType === 'select' || field.fieldType === 'radio' || field.fieldType === 'multiselect') {
    return field.options.length > 0 ? field.options : null;
  }
  if (field.fieldType === 'imageChoiceSingle' || field.fieldType === 'imageChoiceMulti') {
    const cleaned = (field.options || [])
      .map(normalizeImageOption)
      .filter((option) => option.label || option.image);
    return cleaned.length > 0 ? cleaned.map((option) => JSON.stringify(option)) : null;
  }
  return null;
}

function mapFieldToState(field, index) {
  return {
    id: field.id || null,
    uid: field.id || createLocalId(),
    label: field.label || '',
    fieldType: field.fieldType || 'text',
    isRequired: Boolean(field.isRequired),
    sortOrder: field.sortOrder || index + 1,
    placeholder: field.placeholder || '',
    options: (field.fieldType === 'imageChoiceSingle' || field.fieldType === 'imageChoiceMulti')
      ? (field.options || []).map(normalizeImageOption)
      : (field.options || []),
    pendingOption: '',
    maxFileSizeMb: field.maxFileSizeMb || '',
    width: field.width || 'full',
    column: field.column || 'left',
  };
}

function mapFormToState(form) {
  if (!form) {
    return {
      name: '',
      description: '',
      sections: [createEmptySection(1)],
    };
  }

  const sections = form.sections?.length > 0
    ? form.sections.map((section, sectionIndex) => ({
      id: section.id || null,
      uid: section.id || createLocalId(),
      title: section.title || '',
      description: section.description || '',
      sortOrder: section.sortOrder || sectionIndex + 1,
      fields: section.fields?.length > 0 ? section.fields.map(mapFieldToState) : [],
    }))
    : [{
      ...createEmptySection(1),
      fields: form.fields?.length > 0 ? form.fields.map(mapFieldToState) : [createEmptyField(1)],
    }];

  // Bir bölüme bağlı olmayan (sectionId == null) eski alanları kaybetmemek için ilk bölüme ekliyoruz.
  // Aksi halde PUT isteğinde ID'leri gitmeyeceği için backend bu alanları "silinmek istenen" olarak
  // yorumlar ve gönderim mevcutsa 409 Conflict döner.
  if (form.sections?.length > 0 && form.fields?.length > 0) {
    const sectionFieldIds = new Set(
      form.sections.flatMap((section) => (section.fields || []).map((field) => field.id).filter(Boolean))
    );
    const orphanFields = form.fields.filter((field) => field.id && !sectionFieldIds.has(field.id));
    if (orphanFields.length > 0 && sections.length > 0) {
      sections[0] = {
        ...sections[0],
        fields: [...sections[0].fields, ...orphanFields.map(mapFieldToState)],
      };
    }
  }

  return {
    name: form.name || '',
    description: form.description || '',
    sections: normalizeSections(sections),
  };
}

function normalizeSections(sections) {
  return sections.map((section, sectionIndex) => ({
    ...section,
    sortOrder: sectionIndex + 1,
    fields: section.fields.map((field, fieldIndex) => ({ ...field, sortOrder: fieldIndex + 1 })),
  }));
}

export default function FormBuilder({ onSave, initialForm = null, submitLabel = 'Formu kaydet' }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sections, setSections] = useState([createEmptySection(1)]);
  // Önizleme modal durumu
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // Sıralama modal durumu — drag/drop yalnızca burada çalışır
  const [isReorderOpen, setIsReorderOpen] = useState(false);
  // PointerSensor için 6px hareket eşiği — yanlışlıkla tıklamayla sürüklemeyi engeller
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const nextState = mapFormToState(initialForm);
    setName(nextState.name);
    setDescription(nextState.description);
    setSections(nextState.sections);
  }, [initialForm]);

  function updateSection(sectionUid, key, value) {
    setSections((current) => current.map((section) => section.uid === sectionUid ? { ...section, [key]: value } : section));
  }

  function addSection() {
    setSections((current) => normalizeSections([...current, createEmptySection(current.length + 1)]));
  }

  function removeSection(sectionUid) {
    setSections((current) => {
      if (current.length === 1) {
        return current;
      }

      return normalizeSections(current.filter((section) => section.uid !== sectionUid));
    });
  }

  function updateField(sectionUid, fieldUid, key, value) {
    setSections((current) => current.map((section) => section.uid === sectionUid
      ? { ...section, fields: section.fields.map((field) => field.uid === fieldUid ? { ...field, [key]: value } : field) }
      : section));
  }

  function updateFieldType(sectionUid, fieldUid, value) {
    setSections((current) => current.map((section) => section.uid === sectionUid
      ? {
        ...section,
        fields: section.fields.map((field) => {
          if (field.uid !== fieldUid) {
            return field;
          }

          if (value === 'select' || value === 'radio' || value === 'multiselect' || value === 'imageChoiceSingle' || value === 'imageChoiceMulti') {
            // Tip değişiminde uygun olmayan seçenekleri sıfırla
            // Metin tabanlı seçim tipleri (select/radio/multiselect) aralarında seçenekleri koruyabilir
            const isTextChoice = (type) => type === 'select' || type === 'radio' || type === 'multiselect';
            const keepOptions = field.fieldType === value || (isTextChoice(field.fieldType) && isTextChoice(value));
            return { ...field, fieldType: value, options: keepOptions ? field.options : [], pendingOption: '' };
          }
          return { ...field, fieldType: value, options: [], pendingOption: '' };
        }),
      }
      : section));
  }

  function addField(sectionUid) {
    setSections((current) => current.map((section) => section.uid === sectionUid
      ? { ...section, fields: [...section.fields, createEmptyField(section.fields.length + 1)] }
      : section));
  }

  function removeField(sectionUid, fieldUid) {
    setSections((current) => normalizeSections(current.map((section) => {
      if (section.uid !== sectionUid || section.fields.length === 1) {
        return section;
      }

      return { ...section, fields: section.fields.filter((field) => field.uid !== fieldUid) };
    })));
  }

  function addOption(sectionUid, fieldUid) {
    setSections((current) => current.map((section) => section.uid === sectionUid
      ? {
        ...section,
        fields: section.fields.map((field) => {
          if (field.uid !== fieldUid) {
            return field;
          }

          const option = field.pendingOption.trim();
          if (!option || field.options.includes(option)) {
            return { ...field, pendingOption: '' };
          }

          return { ...field, options: [...field.options, option], pendingOption: '' };
        }),
      }
      : section));
  }

  function removeOption(sectionUid, fieldUid, optionIndex) {
    setSections((current) => current.map((section) => section.uid === sectionUid
      ? { ...section, fields: section.fields.map((field) => field.uid === fieldUid ? { ...field, options: field.options.filter((_, index) => index !== optionIndex) } : field) }
      : section));
  }

  function handleOptionKeyDown(event, sectionUid, fieldUid) {
    if (event.key === 'Enter') {
      event.preventDefault();
      addOption(sectionUid, fieldUid);
    }
  }

  function findSectionIndexByFieldUid(currentSections, fieldUid) {
    return currentSections.findIndex((section) => section.fields.some((field) => field.uid === fieldUid));
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith('section-') && overId.startsWith('section-')) {
      const activeSectionUid = activeId.replace('section-', '');
      const overSectionUid = overId.replace('section-', '');
      setSections((current) => {
        const oldIndex = current.findIndex((section) => section.uid === activeSectionUid);
        const newIndex = current.findIndex((section) => section.uid === overSectionUid);
        return normalizeSections(arrayMove(current, oldIndex, newIndex));
      });
      return;
    }

    if (activeId.startsWith('field-') && overId.startsWith('field-')) {
      const activeFieldUid = activeId.replace('field-', '');
      const overFieldUid = overId.replace('field-', '');
      setSections((current) => {
        const nextSections = current.map((section) => ({ ...section, fields: [...section.fields] }));
        const sourceSectionIndex = findSectionIndexByFieldUid(nextSections, activeFieldUid);
        const targetSectionIndex = findSectionIndexByFieldUid(nextSections, overFieldUid);
        if (sourceSectionIndex === -1 || targetSectionIndex === -1) {
          return current;
        }

        const sourceFields = nextSections[sourceSectionIndex].fields;
        const targetFields = nextSections[targetSectionIndex].fields;
        const sourceFieldIndex = sourceFields.findIndex((field) => field.uid === activeFieldUid);
        const targetFieldIndex = targetFields.findIndex((field) => field.uid === overFieldUid);

        if (sourceSectionIndex === targetSectionIndex) {
          nextSections[sourceSectionIndex].fields = arrayMove(sourceFields, sourceFieldIndex, targetFieldIndex);
        } else {
          const [movedField] = sourceFields.splice(sourceFieldIndex, 1);
          targetFields.splice(targetFieldIndex, 0, movedField);
        }

        return normalizeSections(nextSections);
      });
    }
  }

  async function submit(event) {
    event.preventDefault();
    const normalizedSections = normalizeSections(sections);
    const saved = await onSave({
      name,
      description,
      fields: [],
      sections: normalizedSections.map((section) => ({
        id: section.id || null,
        title: section.title,
        description: section.description || null,
        sortOrder: section.sortOrder,
        fields: section.fields.map((field) => ({
          id: field.id || null,
          label: field.label,
          fieldType: field.fieldType,
          isRequired: field.isRequired,
          sortOrder: field.sortOrder,
          placeholder: field.placeholder || null,
          options: buildOptionsForSubmit(field),
          maxFileSizeMb: field.maxFileSizeMb ? Number(field.maxFileSizeMb) : null,
          allowedExtensions: null,
          width: field.width || 'full',
          column: field.width === 'half' ? (field.column || 'left') : 'left',
        })),
      })),
    });
    if (saved === false) {
      return;
    }

    setName('');
    setDescription('');
    setSections([createEmptySection(1)]);
  }

  const totalFields = sections.reduce((sum, section) => sum + section.fields.length, 0);

  // Mevcut tasarımdan, DynamicFormRenderer'ın beklediği şekle uygun bir form nesnesi oluşturur
  function buildPreviewForm() {
    const normalized = normalizeSections(sections);
    return {
      id: 'preview',
      name: name || 'Form önizleme',
      description,
      sections: normalized.map((section) => ({
        id: section.uid,
        title: section.title,
        description: section.description,
        sortOrder: section.sortOrder,
        fields: section.fields.map((field) => ({
          id: field.uid,
          label: field.label || 'Etiketsiz alan',
          fieldType: field.fieldType,
          isRequired: field.isRequired,
          sortOrder: field.sortOrder,
          placeholder: field.placeholder,
          options: field.options || [],
          maxFileSizeMb: field.maxFileSizeMb ? Number(field.maxFileSizeMb) : null,
          allowedExtensions: null,
          width: field.width || 'full',
          column: field.width === 'half' ? (field.column || 'left') : 'left',
        })),
      })),
      fields: [],
    };
  }

  // Önizlemede gönderim kapalı — onSubmit'i no-op olarak iletip false döndürüyoruz
  async function previewNoop() {
    return false;
  }

  return (
    <form className="form-builder" onSubmit={submit}>
      {/* Form meta bilgileri */}
      <div className="builder-meta">
        <div className="field">
          <label>Form adı</label>
          <input required value={name} onChange={(event) => setName(event.target.value)} placeholder="Örn. Saha Denetim Formu" />
        </div>
        <div className="field">
          <label>Açıklama</label>
          <input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Kısa bir açıklama" />
        </div>
      </div>

      {/* Bölüm ve alan sayısı özet bandı */}
      <div className="builder-summary">
        <span className="builder-summary-pill"><Layers size={14} /> {sections.length} bölüm</span>
        <span className="builder-summary-pill"><Plus size={14} /> {totalFields} alan</span>
        <button type="button" className="btn btn-soft builder-summary-preview" onClick={() => setIsReorderOpen(true)}>
          <ArrowUpDown size={14} /> Sıralamayı düzenle
        </button>
        <button type="button" className="btn btn-soft builder-summary-preview" onClick={() => setIsPreviewOpen(true)}>
          <Eye size={14} /> Önizle
        </button>
        <span className="builder-summary-hint">Sıralamayı değiştirmek için “Sıralamayı düzenle” modalını açın.</span>
      </div>

      <div className="section-designer-list">
        {sections.map((section, sectionIndex) => (
          <div key={section.uid} className="section-card">
            <div className="section-card-header">
              <div className="section-title-text">
                <span className="section-index">Bölüm {sectionIndex + 1}</span>
                <h3>{section.title || 'Başlıksız Bölüm'}</h3>
                <span className="muted">{section.fields.length} alan</span>
              </div>
              <button className="btn btn-ghost-danger" type="button" onClick={() => removeSection(section.uid)} title="Bölümü sil" disabled={sections.length === 1}>
                <Trash2 size={16} /> Bölümü sil
              </button>
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Bölüm başlığı</label>
                <input required value={section.title} onChange={(event) => updateSection(section.uid, 'title', event.target.value)} placeholder="Örn. Genel Bilgiler" />
              </div>
              <div className="field">
                <label>Bölüm açıklaması</label>
                <input value={section.description} onChange={(event) => updateSection(section.uid, 'description', event.target.value)} placeholder="Opsiyonel açıklama" />
              </div>
            </div>

            <div className="builder-field-list">
              {section.fields.map((field, fieldIndex) => (
                <div key={field.uid} className="builder-field">
                  <FieldEditor
                    sectionUid={section.uid}
                    fieldIndex={fieldIndex}
                    field={field}
                    onUpdate={updateField}
                    onTypeUpdate={updateFieldType}
                    onAddOption={addOption}
                    onRemoveOption={removeOption}
                    onOptionKeyDown={handleOptionKeyDown}
                    onRemoveField={removeField}
                    canRemove={section.fields.length > 1}
                  />
                </div>
              ))}
            </div>

            <button className="btn btn-soft btn-add-field" type="button" onClick={() => addField(section.uid)}>
              <Plus size={16} /> Bu bölüme alan ekle
            </button>
          </div>
        ))}
      </div>

      {/* Alt aksiyon çubuğu */}
      <div className="builder-actionbar">
        <button className="btn btn-soft" type="button" onClick={addSection}><Plus size={16} /> Yeni bölüm ekle</button>
        <div className="builder-actionbar-right">
          <button className="btn btn-soft" type="button" onClick={() => setIsReorderOpen(true)}><ArrowUpDown size={16} /> Sırala</button>
          <button className="btn btn-soft" type="button" onClick={() => setIsPreviewOpen(true)}><Eye size={16} /> Önizle</button>
          <button className="btn btn-primary builder-save" type="submit"><Save size={16} /> {submitLabel}</button>
        </div>
      </div>

      {isPreviewOpen && (
        <Modal title={`Önizleme — ${name || 'Form'}`} onClose={() => setIsPreviewOpen(false)}>
          <div className="form-preview">
            {description && <p className="muted form-preview-description">{description}</p>}
            <DynamicFormRenderer form={buildPreviewForm()} onSubmit={previewNoop} />
            <p className="muted form-preview-hint">Bu sadece bir önizlemedir, gönderim yapılmaz.</p>
          </div>
        </Modal>
      )}

      {isReorderOpen && (
        <Modal title="Sıralamayı Düzenle" onClose={() => setIsReorderOpen(false)}>
          <ReorderDialog
            sections={sections}
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onClose={() => setIsReorderOpen(false)}
          />
        </Modal>
      )}
    </form>
  );
}

// Sıralama modalı — tüm bölüm ve alanları kompakt satırlarla listeler, drag/drop yalnızca burada aktiftir
function ReorderDialog({ sections, sensors, onDragEnd, onClose }) {
  /* İç içe SortableContext yapısı nedeniyle bölüm sürüklerken
     alan satırları çarpışma hedefi olarak yakalanabiliyor.
     Aktif öğenin tipine göre yalnızca uygun droppable'ları dikkate alıyoruz. */
  function smartCollision(args) {
    const activeId = String(args.active?.id || '');
    const isSection = activeId.startsWith('section-');
    const filtered = {
      ...args,
      droppableContainers: args.droppableContainers.filter((container) => {
        const id = String(container.id);
        return isSection ? id.startsWith('section-') : id.startsWith('field-');
      }),
    };
    return closestCenter(filtered);
  }

  return (
    <div className="reorder-dialog">
      <p className="muted reorder-dialog-intro">
        Bölümleri ve içerdiği alanları tutamaçtan sürükleyerek yeniden sıralayın. Değişiklikler anında uygulanır.
      </p>
      <DndContext sensors={sensors} collisionDetection={smartCollision} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map((section) => `section-${section.uid}`)} strategy={verticalListSortingStrategy}>
          <div className="reorder-section-list">
            {sections.map((section, sectionIndex) => (
              <SortableSectionRow
                key={section.uid}
                section={section}
                sectionIndex={sectionIndex}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="reorder-dialog-actions">
        <button type="button" className="btn btn-primary" onClick={onClose}>Tamam</button>
      </div>
    </div>
  );
}

// Modal içindeki bölüm satırı — başlık + altında alan satırlarını içerir
function SortableSectionRow({ section, sectionIndex }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `section-${section.uid}` });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div className={`reorder-section ${isDragging ? 'is-dragging' : ''}`} ref={setNodeRef} style={style}>
      <div className="reorder-section-head">
        <button type="button" className="drag-handle" aria-label="Bölümü sürükle" {...attributes} {...listeners}>
          <GripVertical size={16} />
        </button>
        <div className="reorder-section-title">
          <span className="section-index">Bölüm {sectionIndex + 1}</span>
          <strong>{section.title || 'Başlıksız Bölüm'}</strong>
          <span className="muted">{section.fields.length} alan</span>
        </div>
      </div>
      <SortableContext items={section.fields.map((field) => `field-${field.uid}`)} strategy={verticalListSortingStrategy}>
        <div className="reorder-field-list">
          {section.fields.map((field, fieldIndex) => (
            <SortableFieldRow key={field.uid} field={field} fieldIndex={fieldIndex} />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

// Modal içindeki alan satırı — kompakt, yalnızca etiket + tip rozetı + tutamak
function SortableFieldRow({ field, fieldIndex }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `field-${field.uid}` });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const typeLabel = fieldTypeOptions.find((option) => option.value === field.fieldType)?.label || field.fieldType;

  return (
    <div className={`reorder-field ${isDragging ? 'is-dragging' : ''}`} ref={setNodeRef} style={style}>
      <button type="button" className="drag-handle" aria-label="Alanı sürükle" {...attributes} {...listeners}>
        <GripVertical size={14} />
      </button>
      <span className="reorder-field-index">#{fieldIndex + 1}</span>
      <span className="reorder-field-label">{field.label || 'Etiketsiz alan'}</span>
      <span className="field-type-chip">{typeLabel}</span>
      {field.isRequired && <span className="field-required-chip">Zorunlu</span>}
    </div>
  );
}

function FieldEditor({ sectionUid, fieldIndex, field, onUpdate, onTypeUpdate, onAddOption, onRemoveOption, onOptionKeyDown, onRemoveField, canRemove }) {
  const typeLabel = fieldTypeOptions.find((option) => option.value === field.fieldType)?.label || field.fieldType;

  return (
    <>
      <div className="builder-field-header">
        <div className="builder-field-title">
          <span className="field-index">#{fieldIndex + 1}</span>
          <strong>{field.label || 'Yeni alan'}</strong>
          <span className="field-type-chip">{typeLabel}</span>
          {field.isRequired && <span className="field-required-chip">Zorunlu</span>}
        </div>
        <button className="btn btn-ghost-danger" type="button" onClick={() => onRemoveField(sectionUid, field.uid)} title="Alanı sil" disabled={!canRemove}>
          <Trash2 size={14} /> Sil
        </button>
      </div>
      <div className="grid-2">
        <div className="field">
          <label>Alan etiketi</label>
          <input required value={field.label} onChange={(event) => onUpdate(sectionUid, field.uid, 'label', event.target.value)} placeholder="Örn. Ad Soyad" />
        </div>
        <div className="field">
          <label>Tip</label>
          <select value={field.fieldType} onChange={(event) => onTypeUpdate(sectionUid, field.uid, event.target.value)}>
            {fieldTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      </div>
      {/* Yerleşim kontrolleri: Genişlik (tam/yarım) ve yarım ise Hizalama (sol/sağ) */}
      <div className="grid-2">
        <div className="field">
          <label>Genişlik</label>
          <div className="layout-toggle">
            <button
              type="button"
              className={`layout-toggle-btn ${field.width === 'full' ? 'is-active' : ''}`}
              onClick={() => onUpdate(sectionUid, field.uid, 'width', 'full')}
              title="Tam genişlik"
            >
              <span className="layout-preview layout-preview-full" /> Tam
            </button>
            <button
              type="button"
              className={`layout-toggle-btn ${field.width === 'half' ? 'is-active' : ''}`}
              onClick={() => onUpdate(sectionUid, field.uid, 'width', 'half')}
              title="Yarım genişlik"
            >
              <span className="layout-preview layout-preview-half" /> Yarım
            </button>
          </div>
        </div>
        <div className="field">
          <label>Hizalama</label>
          <div className={`layout-toggle ${field.width === 'full' ? 'is-disabled' : ''}`}>
            <button
              type="button"
              className={`layout-toggle-btn ${field.column === 'left' ? 'is-active' : ''}`}
              onClick={() => onUpdate(sectionUid, field.uid, 'column', 'left')}
              disabled={field.width === 'full'}
              title="Sol"
            >
              <span className="layout-preview layout-preview-left" /> Sol
            </button>
            <button
              type="button"
              className={`layout-toggle-btn ${field.column === 'right' ? 'is-active' : ''}`}
              onClick={() => onUpdate(sectionUid, field.uid, 'column', 'right')}
              disabled={field.width === 'full'}
              title="Sağ"
            >
              <span className="layout-preview layout-preview-right" /> Sağ
            </button>
          </div>
        </div>
      </div>
      <div className={(field.fieldType === 'select' || field.fieldType === 'radio' || field.fieldType === 'multiselect' || field.fieldType === 'imageChoiceSingle' || field.fieldType === 'imageChoiceMulti') ? 'grid-2' : 'form-grid'}>
        <div className="field">
          <label>Placeholder</label>
          <input value={field.placeholder} onChange={(event) => onUpdate(sectionUid, field.uid, 'placeholder', event.target.value)} placeholder="Yer tutucu metni" />
        </div>
        {(field.fieldType === 'select' || field.fieldType === 'radio' || field.fieldType === 'multiselect') && (
          <div className="field">
            <label>Seçenekler</label>
            <div className="option-editor">
              <div className="option-entry">
                <input value={field.pendingOption} onKeyDown={(event) => onOptionKeyDown(event, sectionUid, field.uid)} onChange={(event) => onUpdate(sectionUid, field.uid, 'pendingOption', event.target.value)} placeholder="Seçenek yazın ve Enter'a basın" />
                <button className="btn btn-soft" type="button" onClick={() => onAddOption(sectionUid, field.uid)}><Plus size={14} /> Ekle</button>
              </div>
              <div className="option-list">
                {field.options.map((option, optionIndex) => (
                  <span className="option-chip" key={`${option}-${optionIndex}`}>
                    {option}
                    <button type="button" onClick={() => onRemoveOption(sectionUid, field.uid, optionIndex)} title="Seçeneği kaldır"><X size={12} /></button>
                  </span>
                ))}
                {field.options.length === 0 && <span className="muted">Henüz seçenek eklenmedi.</span>}
              </div>
            </div>
          </div>
        )}
        {(field.fieldType === 'imageChoiceSingle' || field.fieldType === 'imageChoiceMulti') && (
          <div className="field">
            <label>Görsel seçenekler</label>
            <ImageChoiceEditor
              field={field}
              onChange={(nextOptions) => onUpdate(sectionUid, field.uid, 'options', nextOptions)}
            />
          </div>
        )}
      </div>
      <div className="builder-field-footer">
        <label className={`toggle required-toggle ${field.isRequired ? 'is-on' : ''}`}>
          <input
            type="checkbox"
            checked={field.isRequired}
            onChange={(event) => onUpdate(sectionUid, field.uid, 'isRequired', event.target.checked)}
            aria-label="Zorunlu alan"
          />
          <span className="required-toggle-text">
            <span className="required-toggle-label">Zorunlu alan</span>
            <span className="required-toggle-state" aria-hidden="true">
              {field.isRequired ? 'Aktif' : 'Pasif'}
            </span>
          </span>
          {field.isRequired && <span className="required-toggle-badge" aria-hidden="true">*</span>}
        </label>
      </div>
    </>
  );
}

// Görsel seçim alanı için seçenek düzenleyici — her seçeneğe etiket ve resim ekler
function ImageChoiceEditor({ field, onChange }) {
  const options = field.options || [];

  // Belirli bir indekste seçeneğin alanını günceller
  function updateOption(index, key, value) {
    const next = options.map((option, optionIndex) => (
      optionIndex === index ? { ...option, [key]: value } : option
    ));
    onChange(next);
  }

  // Boş bir seçenek satırı ekler
  function addOption() {
    onChange([...options, { label: '', image: '' }]);
  }

  // Belirli indeksteki seçeneği kaldırır
  function removeOption(index) {
    onChange(options.filter((_, optionIndex) => optionIndex !== index));
  }

  // Dosya seçiminde resmi client-side canvas ile sıkıştırır, S3'e yükler ve URL'yi kaydeder
  async function handleFile(index, file) {
    if (!file) {
      return;
    }
    try {
      // Önce client-side resize + JPEG sıkıştırma uygulayıp upload boyutunu küçültüyoruz
      const blob = await compressImage(file, 800, 0.75);
      const formData = new FormData();
      // Endpoint IFormFile parametresini 'file' adıyla bekliyor
      formData.append('file', blob, 'image-option.jpg');
      const response = await apiRequest('/api/forms/image-options/upload', {
        method: 'POST',
        body: formData,
      });
      if (response?.url) {
        updateOption(index, 'image', response.url);
      }
    } catch (error) {
      // Hata kullanıcıya görünür şekilde bildirilir; konsolda ayrıntı tutulur
      // eslint-disable-next-line no-alert
      alert(error?.message || 'Görsel yüklenirken hata oluştu.');
    }
  }

  return (
    <div className="image-choice-editor">
      {options.length === 0 && (
        <p className="muted image-choice-empty">Henüz görsel seçenek yok. Yeni seçenek ekleyin.</p>
      )}
      <div className="image-choice-grid">
        {options.map((option, index) => (
          <div className="image-choice-item" key={index}>
            <div className="image-choice-thumb">
              {option.image ? (
                <img src={option.image} alt={option.label || `Seçenek ${index + 1}`} />
              ) : (
                <span className="image-choice-placeholder"><ImageIcon size={28} /></span>
              )}
              <label className="image-choice-upload" title="Görsel yükle">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleFile(index, event.target.files?.[0])}
                />
                <Upload size={14} />
              </label>
              <button type="button" className="image-choice-remove" onClick={() => removeOption(index)} title="Seçeneği sil">
                <X size={14} />
              </button>
            </div>
            <input
              className="image-choice-label"
              value={option.label}
              onChange={(event) => updateOption(index, 'label', event.target.value)}
              placeholder={`Etiket ${index + 1}`}
            />
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-soft image-choice-add" onClick={addOption}>
        <Plus size={14} /> Yeni görsel seçenek
      </button>
    </div>
  );
}

